import {
  ExportOutlined,
  EyeOutlined,
  FileExcelOutlined,
  InboxOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Result,
  Row,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  listPendingApprovalIncidenceRequests,
  updateIncidenceRequestStatus,
} from 'api/incidence-request.api';
import { getCategories } from 'api/incidence.api';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { dispatchIncidencePendingSummaryRefresh } from 'hooks/incidencePendingSummary';
import { useResponsive } from 'hooks/useResponsive';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from 'types';
import {
  IncidenceRequestStatus,
  incidenceRequestApiStatusMap,
} from 'types/incidenceRequests';
import { IncidenceApprovalFilters } from './components/IncidenceApprovalFilters';
import { IncidenceRequestForwardConfirmModal } from './components/IncidenceRequestForwardConfirmModal';
import {
  PendingApprovalIncidenceRequest,
  formatDisplayDate,
  mapPendingApprovalIncidenceRequestRecord,
  statusLabels,
} from './components/incidenceRequest.shared';
import 'styles/sass/incidence-requests.scss';

type IncidenceApprovalsTableState = 'empty' | 'empty_filtered' | 'error';

const PAGE_SIZE = 50;

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const downloadReport = (rows: PendingApprovalIncidenceRequest[]) => {
  if (!rows.length) {
    message.info('No hay registros para descargar');
    return;
  }

  const header =
    'Folio,Docente,Numero de empleado,Correo,Tipo de incidencia,Fecha de solicitud,Fecha inicial,Fecha final,Status';
  const body = rows.map((row) =>
    [
      row.folio,
      `"${row.requester.fullName}"`,
      row.requester.employeeNumber,
      row.requester.email,
      `"${row.incidenceType}"`,
      formatDisplayDate(row.requestDate),
      formatDisplayDate(row.startDate),
      formatDisplayDate(row.endDate),
      `"${statusLabels[row.status]}"`,
    ].join(',')
  );
  const csvContent = [header, ...body].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `aprobaciones_incidencias_${dayjs().format('YYYYMMDD')}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const SupervisorIncidenceApprovalsPage = () => {
  const navigate = useNavigate();
  const { mobileOnly } = useResponsive();
  const [requests, setRequests] = useState<PendingApprovalIncidenceRequest[]>(
    []
  );
  const [totalRequests, setTotalRequests] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingRequestsError, setLoadingRequestsError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [folioFilter, setFolioFilter] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('');
  const [incidenceTypeFilter, setIncidenceTypeFilter] = useState<
    string | undefined
  >(undefined);
  const [statusFilter, setStatusFilter] = useState<
    IncidenceRequestStatus | undefined
  >(undefined);
  const [fromDateFilter, setFromDateFilter] = useState<Dayjs | null>(null);
  const [toDateFilter, setToDateFilter] = useState<Dayjs | null>(null);
  const [incidenceTypeOptions, setIncidenceTypeOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [requestPendingForward, setRequestPendingForward] =
    useState<PendingApprovalIncidenceRequest | null>(null);
  const [forwardingRequest, setForwardingRequest] = useState(false);

  const normalizedFolioFilter = folioFilter.trim();
  const normalizedRequesterFilter = requesterFilter.trim();

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    setLoadingRequestsError(false);

    try {
      const response = await listPendingApprovalIncidenceRequests({
        page: currentPage,
        limit: PAGE_SIZE,
        ...(normalizedFolioFilter ? { folio: normalizedFolioFilter } : {}),
        ...(normalizedRequesterFilter
          ? { requester: normalizedRequesterFilter }
          : {}),
        ...(incidenceTypeFilter
          ? { incidenceCategory: incidenceTypeFilter }
          : {}),
        ...(statusFilter
          ? { status: incidenceRequestApiStatusMap[statusFilter] }
          : {}),
        ...(fromDateFilter
          ? { startDate: fromDateFilter.format('YYYY-MM-DD') }
          : {}),
        ...(toDateFilter ? { endDate: toDateFilter.format('YYYY-MM-DD') } : {}),
      });

      setRequests(
        response.data.data.map(mapPendingApprovalIncidenceRequestRecord)
      );
      setTotalRequests(response.data.pagination.total);
    } catch {
      setRequests([]);
      setTotalRequests(0);
      setLoadingRequestsError(true);
    } finally {
      setLoadingRequests(false);
    }
  }, [
    currentPage,
    fromDateFilter,
    incidenceTypeFilter,
    normalizedFolioFilter,
    normalizedRequesterFilter,
    statusFilter,
    toDateFilter,
  ]);

  const loadIncidenceTypes = useCallback(async () => {
    try {
      const response = await getCategories();
      const filteredCategories = response.data
        .filter((category) => category.solicitable)
        .sort((firstCategory, secondCategory) =>
          firstCategory.desc.localeCompare(secondCategory.desc)
        )
        .map((category) => ({
          label: `${category.abbr} - ${category.desc}`,
          value: category._id,
        }));
      setIncidenceTypeOptions(filteredCategories);
    } catch (error) {
      setIncidenceTypeOptions([]);
      message.error(
        getErrorMessage(
          error,
          'No fue posible cargar el catálogo de incidencias solicitables.'
        )
      );
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    void loadIncidenceTypes();
  }, [loadIncidenceTypes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    folioFilter,
    fromDateFilter,
    incidenceTypeFilter,
    requesterFilter,
    statusFilter,
    toDateFilter,
  ]);

  const statusOptions = useMemo(
    () => [
      {
        label: statusLabels.review_supervisor,
        value: 'review_supervisor',
      },
    ],
    []
  );

  const clearFilters = () => {
    setFolioFilter('');
    setRequesterFilter('');
    setIncidenceTypeFilter(undefined);
    setStatusFilter(undefined);
    setFromDateFilter(null);
    setToDateFilter(null);
  };

  const hasActiveFilters = Boolean(
    normalizedFolioFilter ||
    normalizedRequesterFilter ||
    incidenceTypeFilter ||
    statusFilter ||
    fromDateFilter ||
    toDateFilter
  );

  const incidenceApprovalsTableState =
    useMemo<IncidenceApprovalsTableState | null>(() => {
      if (loadingRequests) {
        return null;
      }

      if (loadingRequestsError) {
        return 'error';
      }

      if (requests.length > 0) {
        return null;
      }

      if (totalRequests === 0 && !hasActiveFilters) {
        return 'empty';
      }

      return 'empty_filtered';
    }, [
      hasActiveFilters,
      loadingRequests,
      loadingRequestsError,
      requests.length,
      totalRequests,
    ]);

  const totalPages = Math.max(1, Math.ceil(totalRequests / PAGE_SIZE));
  const downloadReportDisabled =
    loadingRequests || loadingRequestsError || totalRequests === 0;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openDetailPage = (request: PendingApprovalIncidenceRequest) => {
    navigate(`/incidencias/aprobaciones/${request.id}`);
  };

  const closeForwardModal = () => {
    setRequestPendingForward(null);
  };

  const confirmForwardRequest = async () => {
    if (!requestPendingForward) {
      return;
    }

    try {
      setForwardingRequest(true);
      await updateIncidenceRequestStatus(requestPendingForward.id, {
        status: 'WRONG_SUPERVISOR',
      });
      dispatchIncidencePendingSummaryRefresh();
      message.success(
        'La solicitud fue marcada con supervisor incorrecto y enviada a RH.'
      );
      closeForwardModal();
      await loadRequests();
    } catch (error) {
      message.error(
        getErrorMessage(
          error,
          'No fue posible marcar la solicitud como supervisor incorrecto.'
        )
      );
    } finally {
      setForwardingRequest(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await listPendingApprovalIncidenceRequests({
        page: 1,
        limit: totalRequests || PAGE_SIZE,
        ...(normalizedFolioFilter ? { folio: normalizedFolioFilter } : {}),
        ...(normalizedRequesterFilter
          ? { requester: normalizedRequesterFilter }
          : {}),
        ...(incidenceTypeFilter
          ? { incidenceCategory: incidenceTypeFilter }
          : {}),
        ...(statusFilter
          ? { status: incidenceRequestApiStatusMap[statusFilter] }
          : {}),
        ...(fromDateFilter
          ? { startDate: fromDateFilter.format('YYYY-MM-DD') }
          : {}),
        ...(toDateFilter ? { endDate: toDateFilter.format('YYYY-MM-DD') } : {}),
      });

      downloadReport(
        response.data.data.map(mapPendingApprovalIncidenceRequestRecord)
      );
    } catch (error) {
      message.error(
        getErrorMessage(error, 'No fue posible descargar el reporte.')
      );
    }
  };

  const renderIncidenceApprovalsTableState = () => {
    if (!incidenceApprovalsTableState) {
      return null;
    }

    const isErrorState = incidenceApprovalsTableState === 'error';
    const isEmptyState = incidenceApprovalsTableState === 'empty';

    const title = isErrorState
      ? 'Error al cargar la información'
      : isEmptyState
        ? 'No hay información para mostrar'
        : 'No hay resultados para tu búsqueda';

    const description = isErrorState
      ? null
      : isEmptyState
        ? 'Cuando un empleado supervisado por ti solicite una incidencia, aparecerá aquí.'
        : 'Intenta una nueva búsqueda con otros filtros';

    const actionLabel = isErrorState
      ? 'Reintentar'
      : isEmptyState
        ? 'Actualizar bandeja'
        : 'Limpiar filtros';

    const onActionClick = () => {
      if (isErrorState || isEmptyState) {
        void loadRequests();
        return;
      }

      clearFilters();
    };

    return (
      <div
        className="incidence-requests-empty-state"
        data-cy={`approval-request-empty-state-${incidenceApprovalsTableState}`}
      >
        {isErrorState ? (
          <FileExcelOutlined className="incidence-requests-empty-state-icon incidence-requests-empty-state-icon--error" />
        ) : (
          <InboxOutlined className="incidence-requests-empty-state-icon" />
        )}
        <p className="incidence-requests-empty-state-title">{title}</p>
        {description ? (
          <p className="incidence-requests-empty-state-description">
            {description}
          </p>
        ) : null}
        <Button
          type="link"
          className="incidence-requests-empty-state-action"
          onClick={onActionClick}
        >
          {actionLabel}
        </Button>
      </div>
    );
  };

  const columns: ColumnsType<PendingApprovalIncidenceRequest> = [
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      sorter: (firstRequest, secondRequest) =>
        firstRequest.folio.localeCompare(secondRequest.folio),
      render: (folio: string) => <span className="request-folio">{folio}</span>,
    },
    {
      title: 'Docente',
      key: 'requester',
      sorter: (firstRequest, secondRequest) =>
        firstRequest.requester.fullName.localeCompare(
          secondRequest.requester.fullName
        ),
      render: (_, request) => request.requester.fullName,
    },
    {
      title: 'Tipo de incidencia',
      dataIndex: 'incidenceType',
      key: 'incidenceType',
      sorter: (firstRequest, secondRequest) =>
        firstRequest.incidenceType.localeCompare(secondRequest.incidenceType),
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'requestDate',
      key: 'requestDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.requestDate).valueOf() -
        dayjs(secondRequest.requestDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Fecha inicial',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.startDate).valueOf() -
        dayjs(secondRequest.startDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Fecha final',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (firstRequest, secondRequest) =>
        dayjs(firstRequest.endDate).valueOf() -
        dayjs(secondRequest.endDate).valueOf(),
      render: (date: string) => formatDisplayDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (firstRequest, secondRequest) =>
        statusLabels[firstRequest.status].localeCompare(
          statusLabels[secondRequest.status]
        ),
      render: (status: IncidenceRequestStatus) => (
        <Tag
          className={`incidence-request-status incidence-request-status--${status}`}
        >
          {statusLabels[status]}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, request) => (
        <div className="option-buttons">
          <Tooltip title="Ver detalle">
            <Button
              icon={<EyeOutlined />}
              type="text"
              onClick={() => openDetailPage(request)}
              data-cy={`approval-request-view-btn-${request.id}`}
            />
          </Tooltip>
          <Tooltip title="Marcar supervisor incorrecto">
            <Button
              icon={<ExportOutlined />}
              type="text"
              onClick={() => setRequestPendingForward(request)}
              data-cy={`approval-request-forward-btn-${request.id}`}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  if (mobileOnly) {
    return (
      <>
        <PageTitle>Aprobaciones de incidencias</PageTitle>
        <div id="incidence-approvals-page">
          <Result
            status="warning"
            title="Vista disponible solo en escritorio"
            subTitle="Para revisar estas solicitudes debes usar una computadora de escritorio."
            extra={
              <Button type="primary" onClick={() => navigate(-1)}>
                Regresar
              </Button>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>Aprobaciones de incidencias</PageTitle>
      <div id="incidence-approvals-page" data-cy="incidence-approvals-page">
        <Row
          className="employee-incidence-header"
          align="middle"
          justify="space-between"
          gutter={[12, 12]}
        >
          <Col flex="auto">
            <div className="employee-incidence-header-title-wrap">
              <h1 className="title">Aprobaciones de incidencias</h1>
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => void handleDownloadReport()}
              disabled={downloadReportDisabled}
              data-cy="approval-request-download-report-btn"
            >
              Descargar reporte
            </Button>
          </Col>
        </Row>

        <IncidenceApprovalFilters
          folioFilter={folioFilter}
          requesterFilter={requesterFilter}
          incidenceTypeOptions={incidenceTypeOptions}
          statusOptions={statusOptions}
          incidenceTypeFilter={incidenceTypeFilter}
          statusFilter={statusFilter}
          fromDateFilter={fromDateFilter}
          toDateFilter={toDateFilter}
          onFolioFilterChange={setFolioFilter}
          onRequesterFilterChange={setRequesterFilter}
          onIncidenceTypeFilterChange={setIncidenceTypeFilter}
          onStatusFilterChange={(value) =>
            setStatusFilter(value as IncidenceRequestStatus | undefined)
          }
          onFromDateFilterChange={setFromDateFilter}
          onToDateFilterChange={setToDateFilter}
          onClearFilters={clearFilters}
        />

        <Spin
          spinning={loadingRequests}
          tip="Cargando..."
          indicator={<LoadingOutlined />}
        >
          <Table
            bordered
            rowKey="id"
            className="table-ps employee-incidence-requests-table"
            columns={columns}
            dataSource={requests}
            data-cy="incidence-approvals-table"
            locale={{
              emptyText: renderIncidenceApprovalsTableState(),
            }}
            scroll={{ x: 1150 }}
            pagination={{
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              pageSize: PAGE_SIZE,
              total: totalRequests,
              position: ['bottomCenter'],
              showSizeChanger: false,
              showTotal: (total, range) => `${range[1]} de ${total} registros`,
            }}
          />
        </Spin>

        <IncidenceRequestForwardConfirmModal
          open={Boolean(requestPendingForward)}
          onClose={closeForwardModal}
          onConfirm={() => void confirmForwardRequest()}
          confirmLoading={forwardingRequest}
        />
      </div>
    </>
  );
};
